import React from 'react';
import { Box, Button, Link, Typography } from '@mui/material';
import { DefinitionList, EmptyState, Meter, SectionCard, SpecGrid, StatusPill } from '../../components/ui';
import type { SpecItem } from '../../components/ui';
import { sbg } from '../../theme/sbgTokens';
import {
  formatCount,
  formatDate,
  formatDateRange,
  formatDateTime,
  formatDays,
  formatNumber,
  formatPercent,
  MISSING,
} from '../../utils/format';
import type { WfhWeeklyPlan } from '../../utils/timeOffUsage';
import type { Employee } from '../../store/slices/employeeSlice';
import type { TimeOffEntry } from '../../types/timeOff';
import {
  EMPLOYMENT_STATUS_LABELS,
  EntryStatusPill,
  escalationMeta,
  hrs,
  RegionSkeleton,
  typeLabel,
} from './meta';
import { describeEntryText, displayRole } from './text';

/* ------------------------------------------------------------------------ */
/* Work from home                                                             */
/* ------------------------------------------------------------------------ */

export interface NextWfhDay {
  ymd: string;
  pending: boolean;
}

export const WfhCard: React.FC<{
  year: number;
  todayYmd: string;
  eligible: boolean;
  weeklyAllowance: number;
  usedDays: number;
  scheduledDays: number;
  pendingDays: number;
  plan: WfhWeeklyPlan;
  nextDay: NextWfhDay | null;
  loading: boolean;
}> = ({ year, todayYmd, eligible, weeklyAllowance, usedDays, scheduledDays, pendingDays, plan, nextDay, loading }) => {
  const overWeeks = plan.weeksOverAllowance;
  const isEmpty = usedDays === 0 && scheduledDays === 0 && pendingDays === 0;
  const items: SpecItem[] = [
    { label: 'Used', value: formatDays(usedDays), caption: `Approved, through today` },
    { label: 'Scheduled', value: formatDays(scheduledDays), caption: 'Approved, after today' },
    { label: 'Pending', value: formatDays(pendingDays), caption: 'Waiting on approval' },
  ];
  if (eligible) {
    items.push({
      label: 'Weeks over plan',
      value: formatCount(overWeeks, 'week'),
      caption: plan.weeksWithUsage > 0 ? `Most in one week: ${formatDays(plan.highestWeekDays)}` : 'No WFH weeks yet',
    });
  }
  const nextLabel = nextDay
    ? nextDay.ymd === todayYmd
      ? 'Today'
      : formatDate(nextDay.ymd, { weekday: true, short: nextDay.ymd.slice(0, 4) === todayYmd.slice(0, 4) })
    : null;

  return (
    <SectionCard
      title={`Work from home, ${year}`}
      description={
        eligible
          ? `Hybrid · ${formatDays(weeklyAllowance)} a week · 8 hrs counts as 1 day`
          : 'In office · each WFH day needs approval · 8 hrs counts as 1 day'
      }
      action={
        eligible && !loading && overWeeks > 0 ? (
          <StatusPill tone="warning">{`Over plan in ${formatCount(overWeeks, 'week')}`}</StatusPill>
        ) : undefined
      }
    >
      {loading ? (
        <RegionSkeleton label="Loading work from home usage" rows={3} />
      ) : isEmpty ? (
        <Typography variant="body1" component="p" sx={{ color: sbg.text.secondary }}>
          {eligible
            ? `No WFH days booked in ${year} yet. The hybrid plan allows ${formatDays(weeklyAllowance)} a week.`
            : `No WFH days in ${year}. In-office employees need approval for each WFH day.`}
        </Typography>
      ) : (
        <>
          <SpecGrid items={items} minColumnWidth={130} />
          {nextLabel && (
            <Typography variant="body1" component="p" sx={{ color: sbg.text.secondary, mt: 2.5, fontVariantNumeric: 'tabular-nums' }}>
              {'Next WFH day: '}
              <Box component="span" sx={{ color: sbg.text.primary }}>
                {nextLabel}
              </Box>
              {nextDay?.pending ? ' · pending approval' : ''}
            </Typography>
          )}
          <Typography variant="body2" component="p" sx={{ color: sbg.text.secondary, mt: nextLabel ? 1 : 2.5 }}>
            {eligible
              ? 'The weekly plan counts approved WFH days, used and scheduled, by Monday–Sunday week.'
              : 'These are approved exceptions. In-office employees have no WFH allotment, so there is no annual limit to compare against.'}
          </Typography>
        </>
      )}
    </SectionCard>
  );
};

/* ------------------------------------------------------------------------ */
/* WFH override requests                                                      */
/* ------------------------------------------------------------------------ */

const CLAIM_WINDOW_MS = 30 * 60 * 1000;

export const OverrideRequestsCard: React.FC<{ entries: TimeOffEntry[] }> = ({ entries }) => (
  <SectionCard
    flush
    title="WFH override requests"
    description={`${formatCount(entries.length, 'request')} escalated for VP or HR approval`}
  >
    <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
      {entries.map((entry, index) => {
        const info = escalationMeta(entry.reviewReason || '');
        const claimedAtMs = entry.vpClaimedAt ? new Date(entry.vpClaimedAt).getTime() : 0;
        const hasLiveClaim = Boolean(
          claimedAtMs
          && claimedAtMs >= Date.now() - CLAIM_WINDOW_MS
          && !entry.vpDecidedAt
        );
        const isAwaitingVp = entry.reviewReason === 'wfh_escalated_to_vp';
        const details = [
          hrs(entry.hours),
          describeEntryText(entry.notes, entry.originalCalendarSubject).primary,
          isAwaitingVp
            ? hasLiveClaim
              ? 'Claimed for review'
              : entry.vpDeferredTo
                ? 'Deferred to another reviewer'
                : null
            : null,
          isAwaitingVp && entry.vpDecisionDueAt ? `Decision due ${formatDateTime(entry.vpDecisionDueAt)}` : null,
          entry.vpDecisionSource === 'hr_override' ? 'HR or admin override' : null,
        ].filter(Boolean);

        return (
          <Box
            component="li"
            key={entry._id || (entry as any).id || index}
            sx={{
              px: 2.5,
              py: 1.5,
              display: 'grid',
              gap: 0.5,
              borderTop: index > 0 ? `1px solid ${sbg.border.subtle}` : 0,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap' }}>
              <Typography variant="labelMd" component="p" sx={{ color: sbg.text.primary, fontVariantNumeric: 'tabular-nums' }}>
                {formatDateRange(entry.startDate, entry.endDate)}
              </Typography>
              <StatusPill tone={info.tone}>{info.label}</StatusPill>
            </Box>
            <Typography variant="body2" component="p" sx={{ color: sbg.text.secondary, fontVariantNumeric: 'tabular-nums', overflowWrap: 'anywhere' }}>
              {details.join(' · ')}
            </Typography>
          </Box>
        );
      })}
    </Box>
  </SectionCard>
);

/* ------------------------------------------------------------------------ */
/* PTO notice                                                                 */
/* ------------------------------------------------------------------------ */

export interface PtoNoticeStats {
  total: number;
  compliant: number;
  shortNotice: number;
  rate: number;
}

export const PtoNoticeCard: React.FC<{ stats: PtoNoticeStats | null; loading: boolean }> = ({ stats, loading }) => {
  const tone = !stats ? 'brand' : stats.rate >= 80 ? 'brand' : stats.rate >= 50 ? 'warning' : 'danger';
  return (
    <SectionCard
      title="PTO notice"
      description="PTO requests made at least 2 weeks ahead · all on file, excluding rejected"
      action={
        stats && !loading && stats.rate < 80 ? (
          <StatusPill tone={stats.rate >= 50 ? 'warning' : 'danger'}>
            {stats.rate >= 50 ? 'Some short notice' : 'Mostly short notice'}
          </StatusPill>
        ) : undefined
      }
    >
      {loading ? (
        <RegionSkeleton label="Loading PTO notice" rows={3} />
      ) : !stats ? (
        <EmptyState compact title="No PTO requests on file" body="Notice compliance appears here after the first PTO request." />
      ) : (
        <Box sx={{ display: 'grid', gap: 0.5 }}>
          <Typography variant="numericMd" component="p" sx={{ color: sbg.text.primary }}>
            {formatPercent(stats.rate)}
          </Typography>
          <Typography variant="body2" component="p" sx={{ color: sbg.text.secondary, fontVariantNumeric: 'tabular-nums' }}>
            {`${formatNumber(stats.compliant)} of ${formatCount(stats.total, 'request')} with 2+ weeks' notice`}
          </Typography>
          <Meter value={stats.rate} tone={tone} aria-label="PTO requests with 2+ weeks' notice" sx={{ my: 1 }} />
          <Typography variant="body2" component="p" sx={{ color: sbg.text.secondary, fontVariantNumeric: 'tabular-nums' }}>
            {stats.shortNotice > 0
              ? `${formatNumber(stats.shortNotice)} short notice (under 2 weeks)`
              : 'No short-notice requests'}
          </Typography>
        </Box>
      )}
    </SectionCard>
  );
};

/* ------------------------------------------------------------------------ */
/* Record details                                                             */
/* ------------------------------------------------------------------------ */

export const DetailsCard: React.FC<{
  employee: Employee;
  weeklyWfhAllowance: number;
  /** Manager and VP as names when the directory can resolve an email. */
  managerName: string | null;
  vpName: string | null;
}> = ({ employee, weeklyWfhAllowance, managerName, vpName }) => {
  const statusLabel = EMPLOYMENT_STATUS_LABELS[employee.employmentStatus || 'active'] ?? EMPLOYMENT_STATUS_LABELS.active;
  const terminated = employee.employmentStatus && employee.employmentStatus !== 'active' && employee.terminationDate
    ? ` · ${formatDate(employee.terminationDate.slice(0, 10))}`
    : '';
  return (
    <SectionCard title="Details" dense>
      <DefinitionList
        columns={2}
        sx={{
          '& > div:last-of-type': { borderBottom: 0 },
          '@media (min-width: 640px)': { '& > div:nth-last-of-type(-n+2)': { borderBottom: 0 } },
        }}
        items={[
          {
            label: 'Email',
            value: employee.email ? (
              <Link href={`mailto:${employee.email}`} sx={{ overflowWrap: 'anywhere' }}>
                {employee.email}
              </Link>
            ) : MISSING,
          },
          { label: 'Role', value: displayRole(employee.role) || MISSING },
          { label: 'Department', value: employee.department?.name || MISSING },
          { label: 'Team', value: employee.teamName || MISSING },
          { label: 'Manager', value: managerName || MISSING },
          { label: 'VP', value: vpName || MISSING },
          { label: 'Start date', value: employee.startDate ? formatDate(employee.startDate.slice(0, 10)) : MISSING },
          { label: 'Status', value: `${statusLabel}${terminated}` },
          {
            label: 'Work arrangement',
            value: employee.wfhEligible ? `Hybrid, ${formatDays(weeklyWfhAllowance)} WFH a week` : 'In office',
          },
          {
            label: 'Annual allotment',
            value: (
              <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                {`${hrs(employee.ptoAllotment || 0)} PTO · ${hrs(employee.sickTimeAllotment || 0)} sick · `}
                {employee.customAllotment ? 'custom, exempt from tenure policy' : 'tenure policy'}
              </Box>
            ),
          },
        ]}
      />
    </SectionCard>
  );
};

/* ------------------------------------------------------------------------ */
/* Upcoming time off                                                          */
/* ------------------------------------------------------------------------ */

const UPCOMING_LIMIT = 5;

export const UpcomingCard: React.FC<{
  entries: TimeOffEntry[];
  loading: boolean;
  firstName: string;
  onViewHistory: () => void;
}> = ({ entries, loading, firstName, onViewHistory }) => {
  const shown = entries.slice(0, UPCOMING_LIMIT);
  return (
    <SectionCard
      flush
      dense
      title="Upcoming time off"
      description="Approved and pending PTO and sick time, from today on"
      action={
        <Button variant="text" size="small" onClick={onViewHistory}>
          View history
        </Button>
      }
      footer={
        !loading && entries.length > UPCOMING_LIMIT ? (
          <Typography variant="body2" component="p" sx={{ color: sbg.text.secondary }}>
            {`Showing ${UPCOMING_LIMIT} of ${formatCount(entries.length, 'entry', 'entries')}`}
          </Typography>
        ) : undefined
      }
    >
      {loading ? (
        <RegionSkeleton label="Loading upcoming time off" rows={3} sx={{ p: 2 }} />
      ) : shown.length === 0 ? (
        <EmptyState
          compact
          title="Nothing upcoming"
          body={`Approved and pending PTO and sick time for ${firstName} appears here once it's booked.`}
        />
      ) : (
        <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
          {shown.map((entry, index) => (
            <Box
              component="li"
              key={entry._id || (entry as any).id || index}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1.5,
                px: 2,
                py: 1.25,
                borderTop: index > 0 ? `1px solid ${sbg.border.subtle}` : 0,
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="labelMd" component="p" sx={{ color: sbg.text.primary, fontVariantNumeric: 'tabular-nums' }}>
                  {formatDateRange(entry.startDate, entry.endDate)}
                </Typography>
                <Typography variant="body2" component="p" sx={{ color: sbg.text.secondary, fontVariantNumeric: 'tabular-nums' }}>
                  {`${typeLabel(entry.type)} · ${hrs(entry.hours)}`}
                </Typography>
              </Box>
              <EntryStatusPill status={entry.status} />
            </Box>
          ))}
        </Box>
      )}
    </SectionCard>
  );
};
